// §27 — the client half of Reminders: fifteen calls and no state.
//
// The same shape as `kernel/chat` and for the same reason (D-217): the doorbell says *that* a list
// changed and this re-`GET`s it, so there is exactly one code path that produces a list and it is
// the one a cold load uses. A cache here would be a second source of truth for a document **two
// people are editing**, which is the worst possible place to keep one — the first thing it would
// get wrong is an item somebody else ticked.

import type { Reminder, ReminderList, ReminderShare, ShareSubject, UserGroup } from '@hangar/shared';

export interface RemindersServiceOptions {
  fetch?: typeof globalThis.fetch;
}

export class RemindersRequestError extends Error {
  readonly status: number;
  constructor(status: number, path: string) {
    super(`${path} answered ${status}`);
    this.name = 'RemindersRequestError';
    this.status = status;
  }
}

export interface RemindersService {
  lists(): Promise<ReminderList[]>;
  createList(name: string, hue?: number): Promise<ReminderList>;
  renameList(id: string, name: string): Promise<ReminderList>;
  recolourList(id: string, hue: number): Promise<ReminderList>;
  deleteList(id: string): Promise<void>;

  items(listId: string): Promise<Reminder[]>;
  addItem(listId: string, fields: { title: string; notes?: string; dueAt?: number | null }): Promise<Reminder>;
  updateItem(
    id: string,
    patch: { title?: string; notes?: string; dueAt?: number | null; completed?: boolean; position?: number },
  ): Promise<Reminder>;
  deleteItem(id: string): Promise<void>;

  shares(listId: string): Promise<ReminderShare[]>;
  share(listId: string, kind: ShareSubject, id: string, canEdit: boolean): Promise<ReminderShare[]>;
  unshare(listId: string, kind: ShareSubject, id: string): Promise<void>;

  groups(): Promise<UserGroup[]>;
  createGroup(name: string): Promise<UserGroup>;
  deleteGroup(id: string): Promise<void>;
  addMember(groupId: string, ref: string): Promise<UserGroup>;
  removeMember(groupId: string, ref: string): Promise<void>;
}

export function createRemindersService(options: RemindersServiceOptions = {}): RemindersService {
  const doFetch = options.fetch ?? globalThis.fetch.bind(globalThis);

  const json = async <T>(path: string, init?: RequestInit): Promise<T> => {
    const response = await doFetch(path, { credentials: 'same-origin', ...init });
    if (!response.ok) throw new RemindersRequestError(response.status, path);
    if (response.status === 204) return undefined as T;
    return (await response.json()) as T;
  };

  const send = <T>(path: string, method: string, body?: unknown): Promise<T> =>
    json<T>(path, {
      method,
      ...(body === undefined
        ? {}
        : { headers: { 'content-type': 'application/json' }, body: JSON.stringify(body) }),
    });

  const list = '/api/reminders/lists';
  /** A ref carries an `@` and a group id is ours, but both reach a path segment. Encode both. */
  const seg = (value: string): string => encodeURIComponent(value);

  return {
    lists: async () => (await json<{ lists: ReminderList[] }>(list)).lists ?? [],
    createList: async (name, hue) =>
      (await send<{ list: ReminderList }>(list, 'POST', { name, ...(hue === undefined ? {} : { hue }) })).list,
    renameList: async (id, name) =>
      (await send<{ list: ReminderList }>(`${list}/${seg(id)}`, 'PATCH', { name })).list,
    recolourList: async (id, hue) =>
      (await send<{ list: ReminderList }>(`${list}/${seg(id)}`, 'PATCH', { hue })).list,
    deleteList: async (id) => {
      await send<void>(`${list}/${seg(id)}`, 'DELETE');
    },

    items: async (listId) =>
      (await json<{ items: Reminder[] }>(`${list}/${seg(listId)}/items`)).items ?? [],
    addItem: async (listId, fields) =>
      (await send<{ item: Reminder }>(`${list}/${seg(listId)}/items`, 'POST', fields)).item,
    updateItem: async (id, patch) =>
      (await send<{ item: Reminder }>(`/api/reminders/items/${seg(id)}`, 'PATCH', patch)).item,
    deleteItem: async (id) => {
      await send<void>(`/api/reminders/items/${seg(id)}`, 'DELETE');
    },

    shares: async (listId) =>
      (await json<{ shares: ReminderShare[] }>(`${list}/${seg(listId)}/shares`)).shares ?? [],
    share: async (listId, kind, id, canEdit) =>
      (await send<{ shares: ReminderShare[] }>(`${list}/${seg(listId)}/shares`, 'PUT', {
        kind,
        id,
        canEdit,
      })).shares ?? [],
    unshare: async (listId, kind, id) => {
      await send<void>(`${list}/${seg(listId)}/shares/${kind}/${seg(id)}`, 'DELETE');
    },

    groups: async () => (await json<{ groups: UserGroup[] }>('/api/groups')).groups ?? [],
    createGroup: async (name) =>
      (await send<{ group: UserGroup }>('/api/groups', 'POST', { name })).group,
    deleteGroup: async (id) => {
      await send<void>(`/api/groups/${seg(id)}`, 'DELETE');
    },
    addMember: async (groupId, ref) =>
      (await send<{ group: UserGroup }>(`/api/groups/${seg(groupId)}/members/${seg(ref)}`, 'PUT')).group,
    removeMember: async (groupId, ref) => {
      await send<void>(`/api/groups/${seg(groupId)}/members/${seg(ref)}`, 'DELETE');
    },
  };
}
